import { addPointsToActualScore } from "@examspark/common";
import {
  InteractionTypeEnum,
  Question,
  Score,
  ScoringMethodEnum,
  Stem,
} from "@examspark/types";

import * as scoreStemType from "./questions";
import { scoreMatchTable } from "./questions";
import { scoreAssociate } from "./questions/associate";
import { scoreCartesianGraph } from "./questions/cartesian-graph";
import { scoreCartesianInequality } from "./questions/cartesian-inequality";
import { scoreBarChart } from "./questions/chart/bar-chart";
import { scoreLineChart } from "./questions/chart/line-chart";
import { scoreDrawing } from "./questions/drawing";
import { scoreFractionModel } from "./questions/fraction-model";
import { scoreGraphicAssociate } from "./questions/graphic-associate";
import { scoreGraphicGapMatch } from "./questions/graphic-gap-match";
import { scoreGraphicHotspot } from "./questions/graphic-hotspot";
import { scoreGraphicOrder } from "./questions/graphic-order";
import { scoreGraphicPositionObject } from "./questions/graphic-position-object";
import { scoreGraphicSelectPoint } from "./questions/graphic-select-point";
import { scoreNumberLine } from "./questions/graphing/number-line";
import { scoreMatch } from "./questions/match";
import { scoreSequence } from "./questions/order";
import { scoreTextExtended } from "./questions/text-extended";
import { scoreTextGapMatch } from "./questions/text-gap-match";
import { scoreTextInline } from "./questions/text-inline";
import { scoreTextSelectable } from "./questions/text-selectable";

export const newScore: Score = {
  min: 0,
  max: 0,
  isScorable: true,
};

export const scoreItem = (
  expected: Question,
  actual: any,
  options?: Partial<{
    /** return question-level scoring data */
    extended: boolean;
    scoringMethod: ScoringMethodEnum;
  }>,
): any => {
  let itemScore: Score = { ...newScore };
  const partScores: Array<Score> = [];
  let hasUndefinedScore = false;

  if (expected?.content?.canvas) {
    expected.content.canvas.forEach((el: any) => {
      switch (el.type) {
        case "stem": {
          const partScore = scoreStem(el, actual?.[el.id]);
          partScores.push(partScore);
          if (partScore.actual == null) hasUndefinedScore = true;
          break;
        }
      }
    });
  }

  const scoringMethod = options?.scoringMethod ?? expected.scoringMethod;
  switch (scoringMethod) {
    case ScoringMethodEnum.Dichotomous:
      itemScore = scoreItemDichotomous(partScores);
      break;
    case ScoringMethodEnum.Dependent:
      itemScore = scoreItemDependent(partScores);
      break;
    case ScoringMethodEnum.CustomTxStaar:
      itemScore = scoreItemCustomTxStaar(partScores);
      break;
    case ScoringMethodEnum.CustomLimitedPartialCredit:
      itemScore = scoreItemCustomLimitedPartialCredit(partScores);
      break;
    case ScoringMethodEnum.PerQuestion:
    default:
      itemScore = scoreItemPerQuestion(partScores);
      break;
  }

  if (hasUndefinedScore) {
    itemScore.actual = undefined;
    return itemScore;
    // stimulus-only.
  } else if (partScores.length === 0) {
    itemScore.actual = 0;
  }

  // do not allow actual score to exceed max score
  if (itemScore.actual != null && itemScore.actual > itemScore.max) {
    itemScore.actual = itemScore.max;
  }

  return options?.extended
    ? {
        ...itemScore,
        parts: partScores,
      }
    : {
        ...itemScore,
      };
};

/**
 * Translate the per question score to a 1 or 0.
 *
 * @param parts
 */
const scoreItemDichotomous = (parts: Array<Score>) => {
  const score: Score = scoreItemPerQuestion(parts);

  if (score.actual == null) {
    score.max = 1;
    return score;
  }

  if (score.actual < score.max) {
    score.actual = 0;
  } else {
    score.actual = 1;
  }

  score.max = 1; // don't do this until after raw score comparison
  return score;
};

/**
 * Receiving any points on one part is depdendent on having gotten the previous
 * question fully correct.
 */
const scoreItemDependent = (parts: Array<Score>) => {
  const score: Score = { ...newScore };
  let hasUndefinedScore = false;
  let partiallyIncorrect = false;

  for (const p of parts) {
    if (p.actual == null) hasUndefinedScore = true;
    score.min += p.min;
    score.max += p.max;

    if (partiallyIncorrect) {
      continue;
    }

    addPointsToActualScore(score, p.actual);

    if (p.actual != null && p.actual < p.max) {
      // if a previous part was incorrect, don't add any more points to actual
      partiallyIncorrect = true;
    }
  }

  if (hasUndefinedScore) {
    score.actual = undefined;
  }

  return score;
};

/**
 * Receiving any points on one part is depdendent on having gotten the previous
 * question fully correct, capped to TX STAAR limits of 0, 1, or 2.
 *
 * Also score dependently.
 */
const scoreItemCustomTxStaar = (parts: Array<Score>) => {
  const score: Score = { ...newScore };
  let hasUndefinedScore = false;
  let partiallyIncorrect = false;

  for (const p of parts) {
    if (p.actual == null) hasUndefinedScore = true;
    score.min += p.min;
    score.max += p.max;

    if (partiallyIncorrect) {
      // if a previous part was incorrect, don't add any more points to actual
      continue;
    }

    addPointsToActualScore(score, p.actual);

    if (p.actual != null && p.actual < p.max) {
      partiallyIncorrect = true;
    }
  }

  if (hasUndefinedScore) {
    score.actual = undefined;
    score.max = Math.min(2, score.max);
  }

  if (score.actual == null) {
    return score;
  }

  if (score.max > 1) {
    if (score.max === score.actual) {
      score.actual = 2;
      score.max = 2;
    } else if (score.actual >= score.max / 2) {
      score.actual = 1;
      score.max = 2;
    } else {
      score.actual = 0;
      score.max = 2;
    }
  }

  return score;
};

/**
 * Receiving any points on one part is depdendent on having gotten the previous
 * question fully correct, capped to Focus SIS-requested limits of 0, 1, or n.
 *
 * Also score dependently.
 */
const scoreItemCustomLimitedPartialCredit = (parts: Array<Score>) => {
  const score: Score = { ...newScore };
  let hasUndefinedScore = false;
  let partiallyIncorrect = false;

  for (const p of parts) {
    if (p.actual == null) hasUndefinedScore = true;
    score.min += p.min;
    score.max += p.max;

    if (partiallyIncorrect) {
      // if a previous part was incorrect, don't add any more points to actual
      continue;
    }

    addPointsToActualScore(score, p.actual);

    if (p.actual != null && p.actual < p.max) {
      partiallyIncorrect = true;
    }
  }

  if (hasUndefinedScore) {
    score.actual = undefined;
    score.max = Math.min(2, score.max);
  }

  if (score.actual == null) {
    return score;
  }

  if (score.max > 1) {
    if (score.actual === score.max) {
      score.actual = score.max;
    } else if (score.actual > 0) {
      score.actual = 1;
    } else {
      score.actual = 0;
    }
  }

  return score;
};

/**
 * Sum the scores of each part without regard for the outcome of a single
 * question.
 *
 * @param parts
 */
const scoreItemPerQuestion = (parts: Array<Score>) => {
  const score: Score = { ...newScore };
  let hasUndefinedScore = false;

  parts.forEach((p) => {
    score.min += p.min;
    score.max += p.max;
    addPointsToActualScore(score, p.actual);
    if (p.actual == null) hasUndefinedScore = true;
  });

  if (hasUndefinedScore) {
    score.actual = undefined;
  }

  return score;
};

export const scoreStem = (
  expected: Stem.Stem,
  actual: Stem.Response,
): Score => {
  const { config } = expected;
  let score = { ...newScore };
  // console.log("scoring type:", expected.interactionType, expected, actual);
  switch (expected.interactionType) {
    case "choice":
      score = scoreStemType.choice(expected, actual);
      break;
    case "text-extended":
      score = scoreTextExtended(expected, actual);
      break;
    case "text-inline":
      score = scoreTextInline(expected, actual);
      break;
    case "text-gap-match":
      score = scoreTextGapMatch(expected, actual);
      break;
    case "sequencing":
      score = scoreSequence(expected, actual);
      break;
    case "text-selectable":
      score = scoreTextSelectable(expected, actual);
      break;
    case "associate":
      score = scoreAssociate(expected, actual);
      break;
    case "match":
      score = scoreMatch(expected, actual);
      break;
    case "match-table":
      score = scoreMatchTable(expected, actual);
      break;
    case "graphic-hotspot":
      score = scoreGraphicHotspot(expected, actual);
      break;
    case "graphic-gap-match":
      score = scoreGraphicGapMatch(expected, actual);
      break;
    case "graphic-associate":
      score = scoreGraphicAssociate(expected, actual);
      break;
    case "graphic-order":
      score = scoreGraphicOrder(expected, actual);
      break;
    case "drawing":
      score = scoreDrawing(expected, actual);
      break;
    case "cartesian-inequality":
      score = scoreCartesianInequality(expected, actual);
      break;
    case "graphic-position-object":
      score = scoreGraphicPositionObject(expected, actual);
      break;
    case "graphic-select-point":
      score = scoreGraphicSelectPoint(expected, actual);
      break;
    case "fraction-model-construct":
    case "fraction-model":
      score = scoreFractionModel(expected, actual);
      break;
    case "cartesian-graph":
      score = scoreCartesianGraph(expected, actual);
      break;
    case "bar-chart":
      score = scoreBarChart(expected, actual);
      break;
    case "line-chart":
      score = scoreLineChart(expected, actual);
      break;
    case InteractionTypeEnum.NumberLinePlot:
      score = scoreNumberLine(expected, actual);
      break;
    default:
      score.isScorable = false;
      break;
  }

  // ensure score doesn't sit outside of bounds
  if (
    score.actual != null &&
    config?.maxScore &&
    score.actual > Number(config.maxScore)
  ) {
    score.actual = Number(config.maxScore);
  }

  const minScore = config?.minScore || 0;
  if (score.actual != null && score.actual < Number(minScore)) {
    score.actual = Number(minScore);
  }

  return score;
};
